import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { useMemo } from 'react'
import { AdminPostView, ContentView } from '../../api/client'
import { getPlatformDisplayName } from '../../util/types'
import SearchParameters from '../content/search/searchParameters'
import { useContentCardPlumbing } from '../content/useContentCardPlumbing'
import Spinner from '../Spinner'
import { buildThread } from './buildThread'
import ConversationDrawer from './ConversationDrawer'
import ConversationPlaceholder from './ConversationPlaceholder'
import ConversationThread from './ConversationThread'
import GapMarker from './GapMarker'
import { usePostConversationPage, useMoreThreadReplies } from './usePostConversationPage'

type Props = {
    post: Pick<
        AdminPostView,
        'id' | 'accountId' | 'socialMediaServiceShortName' | 'collectedCommentCount'
    >
    searchParameters: SearchParameters
}

const GHOST_BUTTON =
    'inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-800 shadow-sm hover:bg-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500 disabled:opacity-60'

/**
 * A post's comments as conversations: top-level comments
 * newest first, each with its replies, and the comments the filters exclude
 * collapsed into "N comments filtered" markers where they fell. Cards are the
 * feed's ContentCard with the same plumbing, so actions behave as in the feed.
 */
export default function PostConversation({ post, searchParameters }: Props) {
    const conversation = usePostConversationPage(post, searchParameters)
    const {
        items,
        nextCursor,
        matchingTotal,
        matchingCapped,
        canRefresh,
        state,
        fetching,
        awaitingLatest,
        awaitingModeration,
        refreshFound,
        notices
    } = conversation
    const { byRoot, loadMoreReplies } = useMoreThreadReplies(
        conversation.params,
        conversation.resetKey
    )
    const platformName = post.socialMediaServiceShortName
        ? getPlatformDisplayName(post.socialMediaServiceShortName)
        : 'the platform'

    // Every card on the tab, for the shared plumbing. Memoised on what it is built
    // from: the plumbing reseeds its action cache whenever this array changes.
    const allContents = useMemo(() => {
        const contents: ContentView[] = []
        for (const item of items) {
            if (!item.thread) continue
            contents.push(item.thread.root as ContentView)
            const replies = byRoot.get(item.thread.root.id)?.replies ?? item.thread.replies
            for (const reply of replies) contents.push(reply.content as ContentView)
        }
        return contents
    }, [items, byRoot])
    const plumbing = useContentCardPlumbing(allContents, { singleAccountId: post.accountId })
    const cardProps = {
        onContentAction: plumbing.onContentAction,
        profileCache: plumbing.profileCache,
        actionCacheMap: plumbing.actionCacheMap,
        onMarkAsReadClick: plumbing.onMarkAsReadClick,
        onMarkAsUnreadClick: plumbing.onMarkAsUnreadClick
    }

    const noComments = state === 'ready' && items.length === 0 && post.collectedCommentCount === 0
    const noMatches = state === 'ready' && !noComments && matchingTotal === 0
    // Comments are held and some match, yet no thread has a root: every comment
    // answers one Arwen does not hold. Said, so the tab is never blank.
    const noTopLevel = state === 'ready' && !noComments && !noMatches && items.length === 0

    return (
        <div className="flex flex-col gap-2">
            {(fetching || canRefresh) && (
                <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        {fetching && (
                            <ConversationPlaceholder
                                kind="fetching"
                                platformName={platformName}
                                message={`Checking ${platformName} for new comments…`}
                            />
                        )}
                    </div>
                    {canRefresh && (
                        <button
                            type="button"
                            onClick={() => void conversation.refresh()}
                            disabled={fetching}
                            className={GHOST_BUTTON}
                        >
                            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                            Check for new comments
                        </button>
                    )}
                </div>
            )}

            {awaitingLatest && !fetching && (
                <RefreshStatus
                    refreshFound={refreshFound}
                    awaitingModeration={awaitingModeration}
                    platformName={platformName}
                />
            )}

            {notices.map((notice) => (
                <p key={notice} className="text-[13px] text-ink-500">
                    {notice}
                </p>
            ))}

            {matchingCapped && (
                <p className="text-xs text-ink-500">
                    Filters applied to the newest 100,000 comments
                </p>
            )}

            {state === 'loading' && (
                <ConversationPlaceholder
                    kind="loading"
                    platformName={platformName}
                    message="Loading comments"
                />
            )}
            {state === 'error' && (
                <p className="text-sm text-ink-700">
                    Couldn't load the threaded view. Reload the page to try again.
                </p>
            )}
            {noComments && (
                <EmptyState
                    title="No comments yet"
                    detail={`Arwen holds no comments under this post.${
                        canRefresh ? ` Check for new comments asks ${platformName} for any.` : ''
                    }`}
                />
            )}
            {noMatches && (
                <EmptyState
                    title="No comments match these filters"
                    detail="Change or clear the filters above to see this post's comments."
                />
            )}

            {noTopLevel && (
                <EmptyState
                    title="No top-level comments to show"
                    detail="The comments Arwen holds here reply to comments it does not hold. The Chronological tab lists them."
                />
            )}

            {state === 'ready' && !noComments && !noMatches && !noTopLevel && (
                <>
                    <div className="flex flex-col">
                        {items.map((item, index) => {
                            const thread = item.thread
                            if (!thread) {
                                return (
                                    <GapMarker
                                        key={`gap-${index}`}
                                        count={item.gapCount ?? 0}
                                        awaiting={item.gapAwaitingModerationCount ?? 0}
                                        noun="comment"
                                    />
                                )
                            }
                            const rootId = thread.root.id
                            const paged = byRoot.get(rootId)
                            const replies = paged?.replies ?? thread.replies
                            const hasMore = paged?.replies
                                ? paged.nextCursor !== null
                                : thread.hasMoreReplies
                            return (
                                <ConversationThread
                                    key={rootId}
                                    trees={[
                                        buildThread(
                                            thread.root as ContentView,
                                            replies.map((r) => r.content as ContentView)
                                        )
                                    ]}
                                    rootMatches={thread.rootMatches}
                                    rootAwaitingModeration={thread.rootAwaitingModeration}
                                    replyMatches={
                                        new Map(replies.map((r) => [r.content.id, r.matches]))
                                    }
                                    replyAwaitingModeration={
                                        new Set(
                                            replies
                                                .filter((r) => r.awaitingModeration)
                                                .map((r) => r.content.id)
                                        )
                                    }
                                    // Both counts are over every descendant, so what is
                                    // left to load is the total less what is on screen.
                                    moreRepliesCount={Math.max(
                                        thread.replyCount - replies.length,
                                        0
                                    )}
                                    onMoreReplies={
                                        hasMore ? () => void loadMoreReplies(rootId) : undefined
                                    }
                                    moreRepliesLoading={paged?.loading ?? false}
                                    {...cardProps}
                                />
                            )
                        })}
                    </div>
                    {nextCursor && (
                        <div className="mt-2 flex justify-center">
                            <button
                                type="button"
                                onClick={() => void conversation.loadMore()}
                                disabled={conversation.loadingMore}
                                className={GHOST_BUTTON}
                            >
                                {conversation.loadingMore && <Spinner size={4} />}
                                Show more comments
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConversationDrawer {...cardProps} />
        </div>
    )
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
    return (
        <div className="flex flex-col gap-1 rounded-lg border border-dashed border-ink-300 bg-ink-50 p-4 text-sm">
            <span className="font-medium text-ink-900">{title}</span>
            <span className="text-ink-500">{detail}</span>
        </div>
    )
}

/**
 * What the page is waiting on after a refresh. Says what the refresh found, so
 * the moderator is never promised comments that may not exist. On YouTube a
 * count of none proves little: replies arrive through the pipeline after the
 * refresh returns.
 */
function RefreshStatus({
    refreshFound,
    awaitingModeration,
    platformName
}: {
    refreshFound: { newComments: number; platform: string } | null
    awaitingModeration: boolean
    platformName: string
}) {
    let message: string | null = null
    if (refreshFound && refreshFound.newComments > 0) {
        const n = refreshFound.newComments
        message = `${n} new ${n === 1 ? 'comment' : 'comments'} from ${platformName}. ${
            n === 1 ? 'It appears' : 'They appear'
        } here once moderated.`
    } else if (awaitingModeration) {
        message =
            'Some comments are still being moderated. This view updates itself as they finish.'
    } else if (refreshFound?.platform === 'youtube') {
        message = 'Checked YouTube. New replies can take a minute to appear.'
    } else if (refreshFound) {
        message = `No new comments on ${platformName}.`
    }
    if (!message) return null
    return <p className="text-[13px] text-ink-500">{message}</p>
}
